import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import AppLayout from '@/components/layout/AppLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Search, Calendar, ChevronRight } from 'lucide-react';
import { pazientiInCaricoMMG } from '@/data/mockMMGData';

const statoStyle = {
  attivo: { label: 'ADI Attiva', bg: 'bg-emerald-50', text: 'text-emerald-600' },
  'in-scadenza': { label: 'In Scadenza', bg: 'bg-amber-50', text: 'text-amber-600' },
  bozza: { label: 'PAI in Bozza', bg: 'bg-slate-100', text: 'text-slate-500' },
};

const paiStatoStyle = {
  validato: { label: 'PAI Validato', color: '#10B981' },
  'da-validare': { label: 'Da Validare', color: '#F59E0B' },
  bozza: { label: 'Bozza', color: '#94A3B8' },
};

const filters = [
  { key: 'tutti', label: 'Tutti' },
  { key: 'bozza', label: 'PAI in Bozza' },
  { key: 'attivo', label: 'ADI Attiva' },
  { key: 'in-scadenza', label: 'In Scadenza' },
];

export default function MMGPatientList() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('tutti');

  const filtered = useMemo(() => {
    let list = pazientiInCaricoMMG;
    if (filter !== 'tutti') list = list.filter(p => p.stato === filter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(p => p.nome.toLowerCase().includes(q) || p.cf.toLowerCase().includes(q));
    }
    return list;
  }, [search, filter]);

  return (
    <AppLayout role="mmg">
      <div className="space-y-6" data-testid="mmg-patient-list">
        <div>
          <h1 className="text-2xl font-semibold text-foreground" data-testid="patient-list-title">Pazienti in Carico</h1>
          <p className="text-sm text-muted-foreground mt-1">Fascicolo ADI — {pazientiInCaricoMMG.length} pazienti</p>
        </div>

        {/* Search + Filters */}
        <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  className="w-full h-10 pl-9 pr-3 rounded-xl border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  placeholder="Cerca per Nome, Cognome o Codice Fiscale..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="search-input"
                />
              </div>
              <div className="flex gap-1.5 flex-wrap">
                {filters.map(f => (
                  <button
                    key={f.key}
                    onClick={() => setFilter(f.key)}
                    className={`px-3 py-2 rounded-xl text-xs font-semibold transition-colors ${filter === f.key ? 'bg-primary/10 text-primary' : 'bg-muted/30 text-muted-foreground hover:bg-muted/50'}`}
                    data-testid={`filter-${f.key}`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Patient rows */}
        <div className="space-y-2.5" data-testid="patient-rows">
          {filtered.map((p, i) => {
            const initials = p.nome.split(' ').map(w => w[0]).join('');
            const st = statoStyle[p.stato] || statoStyle.attivo;
            const pai = paiStatoStyle[p.paiStato] || paiStatoStyle.validato;
            return (
              <motion.div key={p.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
                <Card
                  className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.06)] rounded-2xl hover:shadow-md transition-shadow cursor-pointer group"
                  onClick={() => navigate(`/mmg/pazienti/${p.id}`)}
                  data-testid={`patient-row-${p.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      {/* Avatar */}
                      <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-700 dark:to-slate-800 flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-bold text-slate-500 dark:text-slate-300">{initials}</span>
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-foreground">{p.nome}</p>
                          <Badge variant="outline" className="text-[9px]">{p.cia}</Badge>
                          <Badge className={`text-[9px] border-0 ${st.bg} ${st.text}`}>{st.label}</Badge>
                        </div>
                        <p className="text-[11px] text-muted-foreground mt-0.5">{p.diagnosi}</p>
                      </div>

                      {/* Meta */}
                      <div className="hidden sm:flex items-center gap-6 flex-shrink-0">
                        <div className="text-right">
                          <p className="text-[10px] text-muted-foreground font-medium">Eta</p>
                          <p className="text-xs font-semibold text-foreground">{p.eta} anni</p>
                        </div>
                        <div className="text-right">
                          <p className="text-[10px] text-muted-foreground font-medium">PAI</p>
                          <p className="text-xs font-semibold" style={{ color: pai.color }}>{pai.label}</p>
                        </div>
                        {p.ultimoAccesso && (
                          <div className="text-right">
                            <p className="text-[10px] text-muted-foreground font-medium">Ultimo accesso</p>
                            <p className="text-xs font-semibold text-foreground flex items-center gap-1"><Calendar className="w-3 h-3 text-muted-foreground" />{p.ultimoAccesso}</p>
                          </div>
                        )}
                      </div>

                      <ChevronRight className="w-4 h-4 text-muted-foreground/40 group-hover:text-primary group-hover:translate-x-0.5 transition-all flex-shrink-0" />
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
          {filtered.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-12" data-testid="no-results">Nessun paziente trovato.</p>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
